import { Box, Flex } from "@chakra-ui/react";
import React, { useContext } from "react";
import { UseModelContext } from "../../../../api/hooks/contexts/useModelContext";
import useAccountModel from "../../../../api/hooks/models/useAccountModel";
import { AccountModel } from "../../../../api/models";
import { ModelScope } from "../../../../api/types";
import { FieldPath } from "../../../../react-hook-form";
import { AccountNameInfo } from "../../../common/accounts/AccountNameInfo";
import { VeriAsyncSelect } from "../../drop-down/VeriAsyncSelect";
import { VeriAccountSelectProps } from "./VeriAccountSelect.types";

const VeriAccountSelect = <T, K extends FieldPath<T>>({
  accountType,
  usePlacement,
  isManager,
  mapper,
  managerId,
  showContactInfo,
  excludeAccountIds,
  filters,
  ...rest
}: VeriAccountSelectProps<T, K>) => {
  const [updateCount, setUpdateCount] = React.useState(0);
  const accountModel = useAccountModel(
    usePlacement === false ? { usePlacement, forcePlacement: {} } : undefined,
  );
  const modelOptions = useContext(UseModelContext);

  const _accountType = Array.isArray(accountType)
    ? accountType
    : accountType
    ? [accountType]
    : undefined;

  const isMallcop = modelOptions.defaults.scope === ModelScope.Mallcop;

  React.useEffect(() => {
    setUpdateCount((count) => count + 1);
  }, [JSON.stringify(excludeAccountIds)]);

  return (
    <VeriAsyncSelect
      updateCounter={updateCount}
      wrapperOptions={() => ({
        borderBottom: "1px solid",
        borderBottomColor: "border.grey",
      })}
      label={"Account"}
      onFilter={async (searchFilter) => {
        return (
          (
            await accountModel.getMany(undefined, {
              "filter[search]": searchFilter,
              ...(_accountType
                ? {
                    [isMallcop ? "accountTypes" : "account_types"]:
                      _accountType.join(","),
                    [isManager ? "account_types" : "accountTypes"]:
                      _accountType.join(","),
                  }
                : undefined),
              ...(isMallcop ? { role: "mallcop" } : undefined),
              ...(managerId ? { manager_id: managerId } : undefined),
              "page[size]": 25,
              "page[number]": 1,
              ...filters,
            })
          )?.data || []
        )
          .filter(
            (account) =>
              !excludeAccountIds?.some(
                (id) => String(id) === String(account.id),
              ),
          )
          .map((account) => ({
            label: account.nameOfRecord || "",
            value: String(account.id),
            ...{
              id: Number(account.id),
              nameOfRecord: account.nameOfRecord,
              mnumber: account.mnumber,
              accountType: account.accountType,
              firstName: account.firstName,
              lastName: account.lastName,
              users: account.users,
              ...mapper?.(account),
            },
          }));
      }}
      renderOption={(option) =>
        showContactInfo ? (
          <Flex w={"100%"} alignItems={"flex-start"}>
            <Box w={"35%"} lineHeight={5}>
              {(option as AccountModel)?.users?.[0]?.name}
            </Box>
            <Box w={"65%"}>
              <AccountNameInfo account={option as AccountModel} />
            </Box>
          </Flex>
        ) : (
          <AccountNameInfo account={option as AccountModel} />
        )
      }
      placeholder={"Search or select an account"}
      {...rest}
    />
  );
};

export default VeriAccountSelect;
